import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { invalidateAdminInboxCounts } from '@/hooks/useAdminInboxCounts';
import { linkQuoteToJob } from '@/hooks/useQuotes';
import { JOB_PREFILL_KEYS, readJobPrefill } from '@/lib/quotes';
import { useQueryClient } from '@tanstack/react-query';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useAuth } from '@/contexts/AuthContext';
import { useCleanerTeamMemberId } from '@/hooks/useCleanerTeamMemberId';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, ChevronLeft, ChevronRight, CalendarIcon, Clock, MapPin, User, X, Users, LayoutGrid, AlertTriangle, Pencil, Repeat } from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, parseISO, getDay } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import DispatchCalendar from '@/components/admin/DispatchCalendar';
import { PersonAvatar, StatusChip, adminButton, getStatusMeta, useAdminPageTitle } from '@/components/admin/ui';
import { EditScopeDialog } from '@/components/admin/recurring/EditScopeDialog';
import { RepeatSettingsFields } from '@/components/admin/recurring/RepeatSettingsFields';
import { RepeatVisitPanel } from '@/components/admin/recurring/RepeatVisitPanel';
import {
  DEFAULT_REPEAT_SETTINGS,
  repeatSettingsError,
  toCreateSeriesInput,
  type RepeatSettings,
} from '@/components/admin/recurring/repeat-settings';
import { errorText, futureEditSummary, visitChangesFromForm } from '@/components/admin/recurring/visit-edit';
import { createSeriesFromJob, invalidateRecurring, updateSeriesFromVisit } from '@/hooks/useRecurringSeries';
import { frequencyFromText, isRecurrenceFrequency, type EditScope } from '@/lib/recurrence';
import { JobWorkPanel } from '@/components/admin/jobs/JobWorkPanel';
import { JobPaymentChip } from '@/components/admin/jobs/JobPaymentChip';
import type { JobPaymentFields } from '@/lib/bookingPayment';
import { useScheduleClashCheck } from '@/hooks/useScheduleClashCheck';
import { ScheduleClashDialog } from '@/components/admin/schedule/ScheduleClashDialog';

interface TeamMember {
  id: string;
  name: string;
  email: string;
}

interface Service {
  id: string;
  name: string;
  // How many cleaners the service inherently needs. Authoritative fallback when
  // a job's own required_cleaners is missing (older pending booking requests).
  resource_required?: number | null;
}

interface JobAssignment {
  team_member_id: string;
  team_members?: TeamMember | null;
}

interface Job {
  id: string;
  title: string;
  description: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  team_member_id: string | null;
  service_id: string | null;
  status: string;
  notes: string | null;
  required_cleaners?: number | null;
  team_members?: TeamMember;
  services?: Service;
  job_assignments?: JobAssignment[];
  // Repeat cleans (see src/lib/recurrence.ts).
  series_id?: string | null;
  series_occurrence_date?: string | null;
  is_exception?: boolean | null;
  // Pay to book (online fixed-price bookings).
  payment_method?: JobPaymentFields['payment_method'];
  payment_status?: JobPaymentFields['payment_status'];
  payment_amount_paid?: JobPaymentFields['payment_amount_paid'];
  payment_total?: JobPaymentFields['payment_total'];
  payment_hold_expires_at?: JobPaymentFields['payment_hold_expires_at'];
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const maybeError = error as { message?: unknown; error_description?: unknown; details?: unknown; code?: unknown };
    if (typeof maybeError.message === 'string' && maybeError.message.trim()) return maybeError.message;
    if (typeof maybeError.error_description === 'string' && maybeError.error_description.trim()) return maybeError.error_description;
    if (typeof maybeError.details === 'string' && maybeError.details.trim()) return maybeError.details;
    if (typeof maybeError.code === 'string' && maybeError.code.trim()) return maybeError.code;
  }
  return 'Unable to complete the schedule action. Please try again.';
};

// Minimal shape needed to resolve who a job is assigned to. Both Job and the
// lightweight UpcomingJob satisfy it, so overlap detection can run over either.
type AssignableJob = {
  team_member_id: string | null;
  job_assignments?: JobAssignment[] | null;
};

// Resolve the set of cleaners assigned to a job. The job_assignments table is
// authoritative; fall back to the legacy single team_member_id for older jobs.
const getAssignedMemberIds = (job: AssignableJob): string[] => {
  const ids = (job.job_assignments ?? [])
    .map((a) => a.team_member_id)
    .filter((id): id is string => Boolean(id));
  if (ids.length === 0 && job.team_member_id) return [job.team_member_id];
  return Array.from(new Set(ids));
};

// Coerce a stored cleaner count into a valid positive integer, or null if it is
// missing/invalid (e.g. 0, null, NaN). Used to pick the real requirement.
const toValidCleanerCount = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
};

// The number of cleaners a job truly requires. Derived from the job's own
// required_cleaners OR the linked service's resource_required — whichever is
// larger and valid. Pending customer booking requests created before
// required_cleaners was persisted carry required_cleaners=1 (or null), so the
// service's resource_required is the authoritative fallback (issue #49). Always
// at least 1.
const getRequiredCleaners = (job: {
  required_cleaners?: number | null;
  services?: { resource_required?: number | null } | null;
}): number => {
  const fromJob = toValidCleanerCount(job.required_cleaners) ?? 1;
  const fromService = toValidCleanerCount(job.services?.resource_required) ?? 1;
  return Math.max(1, fromJob, fromService);
};

// Detect jobs that double-book a cleaner. Two non-cancelled jobs conflict when
// they share at least one assigned cleaner, fall on the same date, and their
// time windows overlap (each starts before the other ends). Assignments come
// from getAssignedMemberIds so job_assignments are authoritative — a stale
// legacy team_member_id can no longer mark jobs with different real assignees as
// clashing, nor hide a multi-cleaner conflict (issue #49).
const findOverlappingJobIds = (
  jobs: Array<AssignableJob & {
    id: string;
    scheduled_date: string;
    start_time: string;
    end_time: string;
    status: string;
  }>
): Set<string> => {
  const toMinutes = (time: string) => {
    const [h, m] = time.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };

  const conflicting = new Set<string>();
  const candidates = jobs
    .filter((job) => job.status !== 'cancelled')
    .map((job) => ({ job, members: new Set(getAssignedMemberIds(job)) }))
    .filter((c) => c.members.size > 0);

  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const a = candidates[i];
      const b = candidates[j];
      if (
        a.job.scheduled_date === b.job.scheduled_date &&
        toMinutes(a.job.start_time) < toMinutes(b.job.end_time) &&
        toMinutes(a.job.end_time) > toMinutes(b.job.start_time) &&
        [...a.members].some((id) => b.members.has(id))
      ) {
        conflicting.add(a.job.id);
        conflicting.add(b.job.id);
      }
    }
  }

  return conflicting;
};

// Resolve the display names of cleaners assigned to a job for compact calendar
// cards. Prefer the live teamMembers list (kept current after edits), then the
// names nested in job_assignments, then the legacy single team_members fallback.
const getAssignedMemberNames = (job: Job, teamMembers: TeamMember[] = []): string[] => {
  const byId = new Map(teamMembers.map((m) => [m.id, m.name]));
  const fromAssignments = new Map(
    (job.job_assignments ?? [])
      .filter((a) => a.team_member_id && a.team_members?.name)
      .map((a) => [a.team_member_id, a.team_members!.name])
  );
  return getAssignedMemberIds(job)
    .map(
      (id) =>
        byId.get(id) ??
        fromAssignments.get(id) ??
        (job.team_member_id === id ? job.team_members?.name : undefined)
    )
    .filter((name): name is string => Boolean(name));
};

// Lightweight job shape for the upcoming horizon (today → +90d): enough to detect
// future overlaps AND render the compact "Upcoming jobs" summary, without
// re-fetching every column. Carries the assigned cleaner + client so the summary
// can label each item.
interface UpcomingJob {
  id: string;
  title: string;
  description?: string | null;
  client_name: string | null;
  client_email?: string | null;
  client_phone?: string | null;
  address?: string | null;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  team_member_id: string | null;
  service_id?: string | null;
  status: string;
  notes?: string | null;
  required_cleaners?: number | null;
  team_members?: { id: string; name: string; email?: string } | null;
  services?: Service | null;
  job_assignments?: JobAssignment[] | null;
  // Not selected by the lightweight scan; the job dialog looks them up.
  series_id?: string | null;
  series_occurrence_date?: string | null;
  is_exception?: boolean | null;
  payment_method?: JobPaymentFields['payment_method'];
  payment_status?: JobPaymentFields['payment_status'];
  payment_amount_paid?: JobPaymentFields['payment_amount_paid'];
  payment_total?: JobPaymentFields['payment_total'];
  payment_hold_expires_at?: JobPaymentFields['payment_hold_expires_at'];
}

interface CustomerMessageThreadSummary {
  booking_id: string | null;
  customer_messages?: Array<{ body: string | null }> | null;
}

interface BusinessHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}


const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7am to 6pm

// Owner/admin category buttons that sit above the schedule summary sections.
// They give a quick, cross-date glance of every Scheduled / In Progress /
// Completed / Unassigned job — separate from the dispatch calendar, which stays
// a focused single-day grid (issue #49). Colours mirror the calendar legend.
const CATEGORY_FILTERS = [
  { key: 'scheduled', label: 'Scheduled', dot: 'bg-amber-400 border-amber-500' },
  { key: 'in_progress', label: 'In Progress', dot: 'bg-sky-400 border-sky-500' },
  { key: 'completed', label: 'Completed', dot: 'bg-emerald-400 border-emerald-500' },
  { key: 'unassigned', label: 'Unassigned', dot: 'bg-gray-300 border-gray-400' },
] as const;
const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_FILTERS.map((f) => [f.key, f.label])
);

type ViewMode = 'team' | 'week';
type CleanerApprovalNotificationType =
  | 'booking_approved'
  | 'reschedule_approved'
  | 'cancellation_approved';

const ScheduleAdmin = () => {
  useAdminPageTitle('Schedule');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const queryClient = useQueryClient();
  const { assertCanWrite } = useReadOnlyGuard();
  const { staffRole } = useAuth();
  const { toast } = useToast();
  // Shared double-booking / working-hours prompt ("Book anyway" / "Pick another time").
  const { confirmSchedule, clashPrompt } = useScheduleClashCheck(orgId);

  // Cleaners get a scoped, read-only view of the schedule: only their own
  // scheduled/active jobs (this week + upcoming). They must not see booking
  // requests, cancelled/rescheduled requests, unassigned jobs, or other
  // cleaners' jobs. Resolve the logged-in cleaner's team_members.id so the
  // summary can filter to their assignments.
  const isCleaner = staffRole === 'cleaner';
  const { data: cleanerTeamMemberId } = useCleanerTeamMemberId(orgId, isCleaner);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>('team');
  // The deep-linked job to auto-open, captured from the URL once so we can drop
  // the `job` query param afterwards and a refresh won't reopen it (issue #49).
  const [focusJobId, setFocusJobId] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  // Owner/admin cross-date category filter (issue #49). When set, the summary
  // sections are replaced by a glanceable list of every matching job across all
  // dates; null keeps the default Booking Requests / This week / Upcoming view.
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [categoryJobs, setCategoryJobs] = useState<Job[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [upcomingJobs, setUpcomingJobs] = useState<UpcomingJob[]>([]);
  const [rescheduledRequestJobIds, setRescheduledRequestJobIds] = useState<Set<string>>(new Set());
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  // Inline "Edit Job" mode for the Week-view job dialog so owners can reschedule
  // a job's core order details (title/client/contact/address/date/time/service)
  // from here — mirrors the Team-view dialog (DispatchCalendar) (issue #44).
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    scheduled_date: '',
    start_time: '',
    end_time: '',
    service_id: '',
  });
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [addCleanerId, setAddCleanerId] = useState('');
  // Accepted quote the Add Job dialog was opened from (linked once the job is saved).
  const [prefillQuoteId, setPrefillQuoteId] = useState<string | null>(null);
  // Add Job "Repeats" block, and the repeat-clean edit-scope prompt.
  const [newJobRepeat, setNewJobRepeat] = useState<RepeatSettings>(DEFAULT_REPEAT_SETTINGS);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeBusy, setScopeBusy] = useState<EditScope | null>(null);
  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
    scheduled_date: '',
    start_time: '09:00',
    end_time: '11:00',
    team_member_id: '',
    service_id: '',
    notes: '',
    required_cleaners: '1',
  });

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  useEffect(() => {
    const requestedDate = searchParams.get('date');
    const requestedView = searchParams.get('view');
    const requestedJob = searchParams.get('job');

    if (requestedDate) {
      const parsedDate = parseISO(requestedDate);
      if (!Number.isNaN(parsedDate.getTime())) {
        setWeekStart(startOfWeek(parsedDate, { weekStartsOn: 1 }));
      }
    }

    if (requestedView === 'team') {
      // Deep link to the Team (dispatch) view — e.g. the dashboard unassigned
      // alert. Date/job/filter are forwarded to DispatchCalendar below.
      setViewMode('team');
    } else if (requestedView === 'week' || requestedDate || requestedJob) {
      setViewMode('week');
    }
  }, [searchParams]);

  // Auto-open a deep-linked job's dialog only once: capture the `job` id, then
  // strip it from the URL (replace, so the back button still works) so a refresh
  // doesn't reopen the dialog (issue #49). view/date/filter are kept so the
  // schedule view itself stays deep-linkable.
  useEffect(() => {
    const requestedJob = searchParams.get('job');
    if (!requestedJob) return;
    setFocusJobId(requestedJob);
    const next = new URLSearchParams(searchParams);
    next.delete('job');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);


  // `?new=job` (the admin top-bar "+ New job" shortcut) opens the Add Job
  // dialog once, then drops the param so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('new') !== 'job' || staffRole == null) return;
    // "Book this job" from an accepted quote prefills the dialog (frequency is
    // carried through on newJob.frequency for the recurring-jobs flow).
    const prefill = readJobPrefill(searchParams);
    if (prefill && !isCleaner && canWrite) {
      setNewJob((prev) => ({ ...prev, ...prefill.fields, ...(prefill.frequency ? { frequency: prefill.frequency } : {}) }));
      setPrefillQuoteId(prefill.quoteId);
      // A quote for a weekly/fortnightly/monthly clean opens as a repeat clean.
      const repeatFrequency = frequencyFromText(prefill.frequency);
      if (repeatFrequency) setNewJobRepeat({ ...DEFAULT_REPEAT_SETTINGS, frequency: repeatFrequency });
    }
    if (!isCleaner && canWrite) setIsAddDialogOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    JOB_PREFILL_KEYS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isCleaner, canWrite, staffRole]);

  // Jobs actually rendered in the current week view. Owners/admins see every
  // loaded job; cleaners get a scoped view (only their own scheduled/active
  // jobs). Conflict detection runs over THIS set — never the raw org-wide jobs —
  // so a card can never be flagged against a partner that isn't itself on screen
  // (issue #49: a job assigned to a cleaner via job_assignments but with a legacy
  // team_member_id pointing elsewhere is hidden, yet was still driving a lone,
  // partnerless conflict marker).
  const visibleJobs = useMemo(() => {
    if (!isCleaner) return jobs;
    if (!cleanerTeamMemberId) return [];
    return jobs.filter(
      (job) =>
        getAssignedMemberIds(job).includes(cleanerTeamMemberId) &&
        job.status !== 'cancelled' &&
        job.status !== 'pending_confirmation' &&
        // Pay to book: not real until it's paid.
        job.status !== 'awaiting_payment'
    );
  }, [jobs, isCleaner, cleanerTeamMemberId]);

  // Detect overlaps among the visible jobs so admins can SEE existing
  // double-bookings, not only get blocked when adding a new one (issue #34).
  // Two non-cancelled jobs conflict when they share a team member and date and
  // their time windows overlap (each starts before the other ends).
  const overlappingJobIds = useMemo(() => findOverlappingJobIds(visibleJobs), [visibleJobs]);

  // Global conflict scan across the upcoming horizon (today → +90 days), not just
  // the loaded week. This lets owners notice future double-bookings while sitting
  // on any week. Same overlap rule as above; we also surface the earliest
  // conflicting date so the compact indicator can point owners to it (issue #34).
  const upcomingConflicts = useMemo(() => {
    const conflictingIds = findOverlappingJobIds(upcomingJobs);

    const nextDate = upcomingJobs
      .filter((job) => conflictingIds.has(job.id))
      .map((job) => job.scheduled_date)
      .sort()[0];

    return { count: conflictingIds.size, nextDate: nextDate ?? null };
  }, [upcomingJobs]);

  // Compact summary above the calendar (QA: the page looked empty). "This week"
  // reuses the already-loaded week jobs; "Upcoming jobs" reuses the upcoming-horizon
  // scan, narrowed to dates AFTER the displayed week, sorted earliest-first and
  // capped so it stays a glanceable list rather than a second calendar.
  const SUMMARY_LIMIT = 5;

  const bookingRequestsSummary = useMemo(() => {
    const byId = new Map<string, Job | UpcomingJob>();
    [...jobs, ...upcomingJobs]
      .filter((job) => job.status === 'pending_confirmation' && !rescheduledRequestJobIds.has(job.id))
      .forEach((job) => byId.set(job.id, job));

    return Array.from(byId.values())
      .sort((a, b) =>
        a.scheduled_date === b.scheduled_date
          ? a.start_time.localeCompare(b.start_time)
          : a.scheduled_date.localeCompare(b.scheduled_date)
      )
      .slice(0, SUMMARY_LIMIT);
  }, [jobs, upcomingJobs, rescheduledRequestJobIds]);

  const thisWeekSummary = useMemo(() => {
    // `jobs` is already scoped to the displayed week and ordered by date/time.
    // Cleaners only ever see their own assigned scheduled/active jobs.
    return jobs
      .filter((job) => job.status !== 'cancelled' && job.status !== 'pending_confirmation')
      .filter((job) => !isCleaner || job.status !== 'awaiting_payment')
      .filter((job) => !isCleaner || (cleanerTeamMemberId ? getAssignedMemberIds(job).includes(cleanerTeamMemberId) : false))
      .slice(0, SUMMARY_LIMIT);
  }, [jobs, isCleaner, cleanerTeamMemberId]);

  const upcomingSummary = useMemo(() => {
    const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');
    return upcomingJobs
      .filter((job) => job.scheduled_date > weekEndStr && job.status !== 'pending_confirmation')
      .filter((job) => !isCleaner || job.status !== 'awaiting_payment')
      .filter((job) => !isCleaner || (cleanerTeamMemberId ? getAssignedMemberIds(job).includes(cleanerTeamMemberId) : false))
      .sort((a, b) =>
        a.scheduled_date === b.scheduled_date
          ? a.start_time.localeCompare(b.start_time)
          : a.scheduled_date.localeCompare(b.scheduled_date)
      )
      .slice(0, SUMMARY_LIMIT);
  }, [upcomingJobs, weekStart, isCleaner, cleanerTeamMemberId]);

  const cancelledOrRescheduledRequests = useMemo(() => {
    const byId = new Map<string, Job | UpcomingJob>();
    [...jobs, ...upcomingJobs]
      .filter((job) => job.status === 'cancelled' || rescheduledRequestJobIds.has(job.id))
      .forEach((job) => byId.set(job.id, job));

    return Array.from(byId.values())
      .sort((a, b) =>
        a.scheduled_date === b.scheduled_date
          ? a.start_time.localeCompare(b.start_time)
          : a.scheduled_date.localeCompare(b.scheduled_date)
      )
      .slice(0, SUMMARY_LIMIT);
  }, [jobs, upcomingJobs, rescheduledRequestJobIds]);

  // Load every job (all dates) for the active category button. This mirrors the
  // cross-date glance the dispatch calendar used to provide before issue #49
  // moved it here — kept as a raw fetch to match this page's existing data flow.
  // Cleaners never see category filters, so this only runs for owners/admins.
  useEffect(() => {
    if (!orgId || !categoryFilter || isCleaner) {
      setCategoryJobs([]);
      setCategoryLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setCategoryLoading(true);
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('*, team_members(id, name, email), services(id, name, resource_required), job_assignments(team_member_id, team_members(id, name, email))')
          .eq('organization_id', orgId)
          .order('scheduled_date')
          .order('start_time');
        if (error) throw error;
        if (!cancelled) setCategoryJobs((data || []) as Job[]);
      } catch (error) {
        if (!cancelled) {
          toast({ title: 'Error', description: getErrorMessage(error), variant: 'destructive' });
          setCategoryJobs([]);
        }
      } finally {
        if (!cancelled) setCategoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, categoryFilter, isCleaner, toast]);

  // The matching jobs for the active category, ordered earliest-first by the
  // query. "unassigned" means no cleaner is assigned (and not cancelled); the
  // others match the job status directly.
  const categoryResults = useMemo(() => {
    if (!categoryFilter) return [];
    if (categoryFilter === 'unassigned') {
      return categoryJobs.filter(
        (job) => getAssignedMemberIds(job).length === 0 && job.status !== 'cancelled'
      );
    }
    return categoryJobs.filter((job) => job.status === categoryFilter);
  }, [categoryFilter, categoryJobs]);

  const getBusinessHoursForDay = (date: Date): BusinessHour | undefined => {
    const dayOfWeek = getDay(date);
    return businessHours.find(h => h.day_of_week === dayOfWeek);
  };

  const isHourOpen = (date: Date, hour: number): boolean => {
    const bh = getBusinessHoursForDay(date);
    if (!bh || !bh.is_open) return false;
    
    const openHour = parseInt(bh.open_time.split(':')[0]);
    const closeHour = parseInt(bh.close_time.split(':')[0]);
    
    return hour >= openHour && hour < closeHour;
  };

  const fetchData = useCallback(async (): Promise<Job[]> => {
    if (!orgId) return [];

    setLoading(true);
    try {
      const weekEnd = addDays(weekStart, 6);
      const today = new Date();

      const [jobsRes, upcomingRes, teamRes, servicesRes, hoursRes] = await Promise.all([
        supabase
          .from('jobs')
          .select('*, team_members(id, name, email), services(id, name, resource_required), job_assignments(team_member_id, team_members(id, name, email))')
          .eq('organization_id', orgId)
          .gte('scheduled_date', format(weekStart, 'yyyy-MM-dd'))
          .lte('scheduled_date', format(weekEnd, 'yyyy-MM-dd'))
          .order('scheduled_date')
          .order('start_time'),
        // Lightweight scan of upcoming jobs to flag future conflicts globally,
        // independent of which week is currently displayed (issue #34).
        supabase
          .from('jobs')
          .select('id, title, description, client_name, client_email, client_phone, address, scheduled_date, start_time, end_time, team_member_id, service_id, status, notes, required_cleaners, payment_method, payment_status, payment_amount_paid, payment_total, payment_hold_expires_at, team_members(id, name, email), services(id, name, resource_required), job_assignments(team_member_id, team_members(id, name, email))')
          .eq('organization_id', orgId)
          .neq('status', 'cancelled')
          .gte('scheduled_date', format(today, 'yyyy-MM-dd'))
          .lte('scheduled_date', format(addDays(today, 90), 'yyyy-MM-dd'))
          .order('scheduled_date')
          .order('start_time'),
        supabase.from('team_members').select('id, name, email').eq('organization_id', orgId).order('name'),
        supabase.from('services').select('id, name').eq('organization_id', orgId).eq('is_active', true).order('name'),
        supabase.from('business_hours').select('day_of_week, is_open, open_time, close_time').eq('organization_id', orgId),
      ]);

      if (jobsRes.error) throw jobsRes.error;
      if (upcomingRes.error) throw upcomingRes.error;
      if (teamRes.error) throw teamRes.error;
      if (servicesRes.error) throw servicesRes.error;
      if (hoursRes.error) throw hoursRes.error;

      const allJobs = [...(jobsRes.data || []), ...(upcomingRes.data || [])];
      const pendingIds = Array.from(new Set(
        allJobs
          .filter((job) => job.status === 'pending_confirmation')
          .map((job) => job.id)
      ));

      let rescheduledIds = new Set<string>();
      if (pendingIds.length > 0) {
        const threadsRes = await supabase
          .from('customer_message_threads')
          .select('booking_id, customer_messages(body)')
          .eq('organization_id', orgId)
          .in('booking_id', pendingIds);

        if (threadsRes.error) throw threadsRes.error;

        rescheduledIds = new Set(
          ((threadsRes.data || []) as CustomerMessageThreadSummary[])
            .filter((thread) =>
              thread.booking_id &&
              (thread.customer_messages || []).some((message) =>
                (message.body || '').toLowerCase().includes('customer rescheduled this booking')
              )
            )
            .map((thread) => thread.booking_id as string)
        );
      }

      const jobsData = (jobsRes.data || []) as Job[];
      setJobs(jobsData);
      // Keep the top-bar bell / sidebar counts in step with request approvals.
      invalidateAdminInboxCounts(queryClient);
      setUpcomingJobs(upcomingRes.data || []);
      setRescheduledRequestJobIds(rescheduledIds);
      setTeamMembers(teamRes.data || []);
      setServices(servicesRes.data || []);
      setBusinessHours(hoursRes.data || []);
      return jobsData;
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
      return [];
    } finally {
      setLoading(false);
    }
  }, [orgId, queryClient, toast, weekStart]);

  useEffect(() => {
    if (orgLoading) return;

    if (!orgId) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [fetchData, orgId, orgLoading]);

  const handlePrevWeek = () => setWeekStart((prev) => addDays(prev, -7));
  const handleNextWeek = () => setWeekStart((prev) => addDays(prev, 7));
  const handleToday = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const handleJumpToNextConflict = () => {
    if (!upcomingConflicts.nextDate) return;
    setWeekStart(startOfWeek(parseISO(upcomingConflicts.nextDate), { weekStartsOn: 1 }));
    setViewMode('week');
  };


  const notifyAssignedCleaner = async (
    jobId: string,
    notificationType: 'assignment' | CleanerApprovalNotificationType = 'assignment',
    // The specific newly-assigned cleaner for a multi-cleaner job. Without it the
    // edge function resolves the recipient from the legacy jobs.team_member_id —
    // which only holds one member — so the second cleaner added would never be
    // notified. Only meaningful for 'assignment'; approvals stay customer-facing.
    teamMemberId?: string,
  ) => {
    try {
      const { error: notifyError } = await supabase.functions.invoke('send-job-assigned', {
        body: {
          recordId: jobId,
          organizationId: orgId,
          notificationType,
          ...(teamMemberId ? { teamMemberId } : {}),
        },
      });
      if (notifyError) {
        console.error(`Failed to send ${notificationType} notification`, notifyError);
      }
    } catch (notifyError) {
      console.error(`Failed to send ${notificationType} notification`, notifyError);
    }
  };

  const handleAddJob = async () => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    if (!newJob.title || !newJob.scheduled_date || !newJob.start_time || !newJob.end_time) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const newStart = toMinutes(newJob.start_time);
    const newEnd = toMinutes(newJob.end_time);

    if (newEnd <= newStart) {
      toast({ title: 'Invalid time range', description: 'End time must be after start time.', variant: 'destructive' });
      return;
    }

    const repeatProblem = repeatSettingsError(newJobRepeat, newJob.scheduled_date);
    if (repeatProblem) {
      toast({ title: 'Check the repeat settings', description: repeatProblem, variant: 'destructive' });
      return;
    }

    const requiredCleaners = Math.max(1, parseInt(newJob.required_cleaners, 10) || 1);

    // Warn (never silently) about double bookings, the buffer between jobs, the
    // cleaner's availability / time off and opening hours. The check loads that
    // day's jobs, so it isn't limited to the week on screen.
    setSaving(true);
    const okToBook = await confirmSchedule(
      {
        date: newJob.scheduled_date,
        start: newJob.start_time,
        end: newJob.end_time,
        memberIds: newJob.team_member_id ? [newJob.team_member_id] : [],
      },
      { fallbackJobs: jobs, fallbackMembers: teamMembers, repeating: isRecurrenceFrequency(newJobRepeat.frequency) },
    );
    if (!okToBook) {
      setSaving(false);
      return;
    }

    try {
      const { data: inserted, error } = await supabase
        .from('jobs')
        .insert({
          title: newJob.title,
          description: newJob.description || null,
          client_name: newJob.client_name || null,
          client_email: newJob.client_email || null,
          client_phone: newJob.client_phone || null,
          address: newJob.address || null,
          scheduled_date: newJob.scheduled_date,
          start_time: newJob.start_time,
          end_time: newJob.end_time,
          team_member_id: newJob.team_member_id || null,
          service_id: newJob.service_id || null,
          notes: newJob.notes || null,
          status: 'scheduled',
          required_cleaners: requiredCleaners,
          organization_id: orgId,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Mirror the initial cleaner into the many-to-many table so the new model
      // is the source of truth from creation (legacy team_member_id is kept too).
      if (inserted?.id && newJob.team_member_id) {
        const { error: assignErr } = await supabase
          .from('job_assignments')
          .insert({ job_id: inserted.id, team_member_id: newJob.team_member_id, organization_id: orgId });
        if (assignErr) throw assignErr;
        await notifyAssignedCleaner(inserted.id, 'assignment', newJob.team_member_id);
      }

      if (inserted?.id && prefillQuoteId && orgId) {
        try {
          await linkQuoteToJob(orgId, prefillQuoteId, inserted.id);
          invalidateAdminInboxCounts(queryClient);
        } catch (linkError) {
          console.error('Could not link the job to its quote', linkError);
        }
        setPrefillQuoteId(null);
      }

      // "Repeats": turn the new job into the first visit of a repeat clean. The
      // job is already saved, so a failure here leaves a one-off job and says so.
      const seriesInput = inserted?.id ? toCreateSeriesInput(inserted.id, newJobRepeat) : null;
      if (seriesInput) {
        try {
          await createSeriesFromJob(seriesInput);
          invalidateRecurring(queryClient);
          toast({ title: 'Repeat clean added', description: 'The first visit and the next 12 weeks are on the calendar.' });
        } catch (seriesError) {
          toast({
            title: 'Job added, but it isn’t repeating yet',
            description: `${errorText(seriesError)} You can set it to repeat from the job.`,
            variant: 'destructive',
          });
        }
      } else {
        toast({ title: 'Success', description: 'Job added to schedule' });
      }
      setIsAddDialogOpen(false);
      setNewJobRepeat(DEFAULT_REPEAT_SETTINGS);
      setNewJob({
        title: '',
        description: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        address: '',
        scheduled_date: '',
        start_time: '09:00',
        end_time: '11:00',
        team_member_id: '',
        service_id: '',
        notes: '',
        required_cleaners: '1',
      });
      setSelectedDate(undefined);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateJobStatus = async (jobId: string, status: string) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    try {
      const { error } = await supabase.from('jobs').update({ status }).eq('id', jobId);
      if (error) throw error;

      const updateStatus = <T extends { id: string; status: string }>(items: T[]) =>
        items.map((job) => (job.id === jobId ? { ...job, status } : job));

      setJobs((prev) => updateStatus(prev));
      setUpcomingJobs((prev) => updateStatus(prev));
      setCategoryJobs((prev) => updateStatus(prev));
      setSelectedJob((prev) => (prev?.id === jobId ? { ...prev, status } : prev));
      toast({ title: 'Success', description: 'Job status updated' });
      fetchData();
      setIsViewDialogOpen(false);
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const handleApproveRequest = async (job: Job | UpcomingJob) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    // Centralized approval guard for every path (summary card + detail modal):
    // a multi-cleaner service cannot be approved until it is fully staffed. The
    // real requirement is derived from required_cleaners OR the service's
    // resource_required, so services like Commercial Deep Cleaning (needs 2) are
    // never approved understaffed, even for pending requests created before
    // required_cleaners was persisted (issue #49). Single-cleaner bookings keep
    // the existing "approve now, assign a cleaner later" flow — the customer is
    // still notified server-side.
    const required = getRequiredCleaners(job);
    const assignedCount = getAssignedMemberIds(job).length;
    if (required > 1 && assignedCount < required) {
      const remaining = required - assignedCount;
      toast({
        title: 'Cannot approve yet',
        description: `Assign ${remaining} more cleaner${remaining === 1 ? '' : 's'} before approving (${assignedCount}/${required} assigned).`,
        variant: 'destructive',
      });
      return;
    }

    // Pick the customer-facing notification for what's being approved. A plain
    // pending booking request is its own type (was wrongly treated as a
    // reschedule), distinct from an actual reschedule or a cancellation request.
    const notificationType: CleanerApprovalNotificationType =
      job.status === 'cancelled'
        ? 'cancellation_approved'
        : rescheduledRequestJobIds.has(job.id)
          ? 'reschedule_approved'
          : 'booking_approved';

    try {
      // Normalize required_cleaners to the derived requirement on approval so
      // older requests created before it was persisted stop reading as 1.
      const { error } = await supabase
        .from('jobs')
        .update({ status: 'confirmed', required_cleaners: required })
        .eq('id', job.id);
      if (error) throw error;
      // Always notify: the customer is emailed even when no cleaner is assigned
      // yet, and the edge function resolves every recipient server-side.
      await notifyAssignedCleaner(job.id, notificationType);
      toast({ title: 'Success', description: 'Job status updated' });
      fetchData();
      setIsViewDialogOpen(false);
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  // Switch the Week-view dialog into edit mode, seeding the form from the
  // selected job. Times are trimmed to HH:mm for the <input type="time"> controls.
  const startEditJob = () => {
    if (!selectedJob) return;
    setEditForm({
      title: selectedJob.title,
      client_name: selectedJob.client_name || '',
      client_email: selectedJob.client_email || '',
      client_phone: selectedJob.client_phone || '',
      address: selectedJob.address || '',
      scheduled_date: selectedJob.scheduled_date,
      start_time: selectedJob.start_time.slice(0, 5),
      end_time: selectedJob.end_time.slice(0, 5),
      service_id: selectedJob.service_id || '',
    });
    setIsEditing(true);
  };

  const handleSaveEdit = async (scope?: EditScope) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    if (!selectedJob) return;

    if (!editForm.title || !editForm.scheduled_date || !editForm.start_time || !editForm.end_time) {
      toast({ title: 'Error', description: 'Please fill in required fields', variant: 'destructive' });
      return;
    }

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const newStart = toMinutes(editForm.start_time);
    const newEnd = toMinutes(editForm.end_time);

    if (newEnd <= newStart) {
      toast({ title: 'Invalid time range', description: 'End time must be after start time.', variant: 'destructive' });
      return;
    }

    // Rescheduling keeps the job's current cleaners, so check each of them on
    // the (possibly new) date and time, excluding the job itself. Asked once,
    // before the repeat-clean scope question.
    if (!scope) {
      const okToMove = await confirmSchedule(
        {
          date: editForm.scheduled_date,
          start: editForm.start_time,
          end: editForm.end_time,
          memberIds: getAssignedMemberIds(selectedJob),
          excludeJobId: selectedJob.id,
        },
        { fallbackJobs: jobs, fallbackMembers: teamMembers },
      );
      if (!okToMove) return;
    }

    // Repeat-clean visit: ask "this visit only" or "this and all future".
    if (selectedJob.series_id && !scope) {
      setScopeOpen(true);
      return;
    }

    setSaving(true);
    if (scope) setScopeBusy(scope);
    try {
      if (scope === 'future') {
        const result = await updateSeriesFromVisit(selectedJob.id, visitChangesFromForm(editForm));
        invalidateRecurring(queryClient);
        toast({ title: 'Repeat clean updated', description: futureEditSummary(result) });
      } else {
        const { error } = await supabase
          .from('jobs')
          .update({
            title: editForm.title,
            client_name: editForm.client_name || null,
            client_email: editForm.client_email || null,
            client_phone: editForm.client_phone || null,
            address: editForm.address || null,
            scheduled_date: editForm.scheduled_date,
            start_time: editForm.start_time,
            end_time: editForm.end_time,
            service_id: editForm.service_id || null,
            // "This visit only": detach it so regenerating the series leaves it alone.
            ...(selectedJob.series_id ? { is_exception: true } : {}),
          })
          .eq('id', selectedJob.id);
        if (error) throw error;

        // Manual owner edits are treated as schedule changes for the assigned cleaner.
        if (selectedJob.team_member_id) {
          await notifyAssignedCleaner(selectedJob.id, 'reschedule_approved');
        }

        toast({ title: 'Success', description: 'Job details updated' });
      }
      setScopeOpen(false);
      setIsEditing(false);
      setIsViewDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
      setScopeBusy(null);
    }
  };

  // Quick single-cleaner assignment used by the inline team-view dropdown.
  const handleUpdateJobAssignment = async (jobId: string, teamMemberId: string | null) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    const job = [...jobs, ...upcomingJobs].find((j) => j.id === jobId);
    if (teamMemberId && job) {
      const okToAssign = await confirmSchedule(
        { date: job.scheduled_date, start: job.start_time, end: job.end_time, memberIds: [teamMemberId], excludeJobId: jobId },
        { fallbackJobs: jobs, fallbackMembers: teamMembers },
      );
      if (!okToAssign) return;
    }
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ team_member_id: teamMemberId })
        .eq('id', jobId);
      if (error) throw error;

      // Notify the newly-assigned cleaner. Include the selected member so the
      // edge function can target the exact new assignee, not a stale primary.
      if (teamMemberId) {
        await notifyAssignedCleaner(jobId, 'assignment', teamMemberId);
      }

      toast({ title: 'Success', description: 'Team member assigned' });
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  // Re-point the open dialog at the freshly-fetched job after add/remove. `list`
  // only holds the displayed week, so a future/upcoming request or a cross-date
  // category job won't be in it — falling back to `prev` would leave the modal
  // showing a stale cleaner count and make "Add cleaner" look frozen. When the
  // open job isn't in the week list, fetch it directly so its job_assignments
  // and required_cleaners refresh from any source (issue #49).
  const syncSelectedJob = async (list: Job[]) => {
    const openId = selectedJob?.id;
    if (!openId) return;

    const fromWeek = list.find((j) => j.id === openId);
    if (fromWeek) {
      setSelectedJob((prev) => (prev && prev.id === openId ? fromWeek : prev));
      return;
    }

    const { data, error } = await supabase
      .from('jobs')
      .select('*, team_members(id, name, email), services(id, name, resource_required), job_assignments(team_member_id, team_members(id, name, email))')
      .eq('id', openId)
      .maybeSingle();
    if (!error && data) {
      setSelectedJob((prev) => (prev && prev.id === openId ? (data as Job) : prev));
    }
  };

  const handleAddCleaner = async (jobId: string, teamMemberId: string) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    if (!teamMemberId) return;
    // Adding a second (third…) cleaner: check the new cleaner against that slot.
    const target = selectedJob?.id === jobId ? selectedJob : jobs.find((j) => j.id === jobId);
    setSaving(true);
    if (target) {
      const okToAssign = await confirmSchedule(
        { date: target.scheduled_date, start: target.start_time, end: target.end_time, memberIds: [teamMemberId], excludeJobId: jobId },
        { fallbackJobs: jobs, fallbackMembers: teamMembers },
      );
      if (!okToAssign) {
        setSaving(false);
        return;
      }
    }
    try {
      const { error } = await supabase
        .from('job_assignments')
        .insert({ job_id: jobId, team_member_id: teamMemberId, organization_id: orgId });
      if (error) throw error;

      // Keep the legacy single field populated when it was empty so older reads
      // still resolve a cleaner.
      const job = jobs.find((j) => j.id === jobId);
      if (job && !job.team_member_id) {
        await supabase.from('jobs').update({ team_member_id: teamMemberId }).eq('id', jobId);
      }

      // Notify the newly-assigned cleaner. The browser names which member was
      // just added (teamMemberId) so a 2-cleaner job notifies the SECOND cleaner,
      // not the legacy primary cleaner again; the edge function validates the
      // member is assigned to this job and resolves the recipient + content.
      if (teamMemberId) {
        await notifyAssignedCleaner(jobId, 'assignment', teamMemberId);
      }

      toast({ title: 'Success', description: 'Cleaner assigned' });
      setAddCleanerId('');
      const list = await fetchData();
      await syncSelectedJob(list);
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCleaner = async (jobId: string, teamMemberId: string) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('job_assignments')
        .delete()
        .eq('job_id', jobId)
        .eq('team_member_id', teamMemberId);
      if (error) throw error;

      // If we removed the legacy "primary" cleaner, repoint team_member_id at a
      // remaining assignee (or clear it) so the two stay consistent.
      const job = jobs.find((j) => j.id === jobId);
      if (job && job.team_member_id === teamMemberId) {
        const remaining = getAssignedMemberIds(job).filter((id) => id !== teamMemberId);
        await supabase.from('jobs').update({ team_member_id: remaining[0] ?? null }).eq('id', jobId);
      }

      toast({ title: 'Success', description: 'Cleaner removed' });
      const list = await fetchData();
      await syncSelectedJob(list);
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (isCleaner) return;
    if (!assertCanWrite()) return;
    if (!confirm('Are you sure you want to delete this job?')) return;
    try {
      const { error } = await supabase.from('jobs').delete().eq('id', jobId);
      if (error) throw error;
      toast({ title: 'Success', description: 'Job deleted' });
      setIsViewDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = getErrorMessage(error);
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const openJobDetails = (job: Job | UpcomingJob) => {
    setSelectedJob({
      id: job.id,
      title: job.title,
      description: job.description ?? null,
      client_name: job.client_name ?? null,
      client_email: job.client_email ?? null,
      client_phone: job.client_phone ?? null,
      address: job.address ?? null,
      scheduled_date: job.scheduled_date,
      start_time: job.start_time,
      end_time: job.end_time,
      team_member_id: job.team_member_id,
      service_id: job.service_id ?? null,
      status: job.status,
      notes: job.notes ?? null,
      // Preserve the multi-cleaner fields so the dialog can show the real
      // requirement and assignees — and so the approval guard reads the correct
      // count — for future/upcoming and category requests too (issue #49).
      required_cleaners: job.required_cleaners ?? null,
      job_assignments: job.job_assignments ?? undefined,
      team_members: job.team_members ? {
        id: job.team_members.id,
        name: job.team_members.name,
        email: job.team_members.email ?? '',
      } : undefined,
      services: job.services ?? undefined,
      // Left undefined when the source row didn't load them (the upcoming scan),
      // so the repeat panel knows to look them up.
      series_id: 'series_id' in job ? job.series_id ?? null : undefined,
      series_occurrence_date: job.series_occurrence_date,
      is_exception: job.is_exception,
    });
    setIsEditing(false);
    setIsViewDialogOpen(true);
  };

  // The repeat panel looked up a job's series link (upcoming-scan rows don't
  // carry it): merge it so Edit Job asks the repeat-clean scope question.
  const handleSeriesLinkResolved = useCallback(
    (link: { id: string; series_id?: string | null; series_occurrence_date?: string | null; is_exception?: boolean | null }) =>
      setSelectedJob((prev) => (prev && prev.id === link.id ? { ...prev, ...link } : prev)),
    [],
  );

  // Render the visible jobs for a given day. `visibleJobs` already applies the
  // cleaner scoping (own scheduled/active jobs only) used by overlap detection,
  // so rendering and conflict markers always agree on what's on screen.
  const getJobsForDay = (date: Date) => {
    return visibleJobs.filter((job) => isSameDay(parseISO(job.scheduled_date), date));
  };

  const getJobPosition = (job: Job) => {
    const startHour = parseInt(job.start_time.split(':')[0]);
    const startMinute = parseInt(job.start_time.split(':')[1]);
    const endHour = parseInt(job.end_time.split(':')[0]);
    const endMinute = parseInt(job.end_time.split(':')[1]);

    const top = ((startHour - 7) * 60 + startMinute) * (60 / 60); // 60px per hour
    const height = ((endHour - startHour) * 60 + (endMinute - startMinute)) * (60 / 60);

    return { top: `${top}px`, height: `${Math.max(height, 30)}px` };
  };

  // Fan-out overlapping same-day jobs into side-by-side lanes so clashing blocks
  // stay individually visible/clickable instead of stacking full-width on the
  // same column (mirrors DispatchCalendar). Jobs are grouped into clusters of
  // mutual overlap; within a cluster each job takes the first free lane (greedy)
  // and every block is sized to the cluster's lane count so widths line up.
  // Non-overlapping jobs end up alone → a single full-width lane.
  const getJobLanes = (dayJobs: Job[]) => {
    const toMinutes = (time: string) => {
      const [h, m] = time.split(':');
      return parseInt(h, 10) * 60 + parseInt(m, 10);
    };

    const layout = new Map<string, { lane: number; lanes: number }>();
    const sorted = [...dayJobs].sort((a, b) =>
      toMinutes(a.start_time) - toMinutes(b.start_time) ||
      toMinutes(a.end_time) - toMinutes(b.end_time)
    );

    let cluster: Job[] = [];
    let clusterEnd = -Infinity;

    const flush = () => {
      if (cluster.length === 0) return;
      const laneEnds: number[] = [];
      const laneOf = new Map<string, number>();
      for (const job of cluster) {
        const start = toMinutes(job.start_time);
        let lane = laneEnds.findIndex((end) => end <= start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(toMinutes(job.end_time));
        } else {
          laneEnds[lane] = toMinutes(job.end_time);
        }
        laneOf.set(job.id, lane);
      }
      for (const job of cluster) {
        layout.set(job.id, { lane: laneOf.get(job.id) ?? 0, lanes: laneEnds.length });
      }
      cluster = [];
      clusterEnd = -Infinity;
    };

    for (const job of sorted) {
      if (cluster.length > 0 && toMinutes(job.start_time) >= clusterEnd) flush();
      cluster.push(job);
      clusterEnd = Math.max(clusterEnd, toMinutes(job.end_time));
    }
    flush();

    return layout;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const renderJobMeta = (job: Job | UpcomingJob) => (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <CalendarIcon className="h-3 w-3" />
        {format(parseISO(job.scheduled_date), 'EEE, MMM d')} · {job.start_time.slice(0, 5)}
      </span>
      <span className="inline-flex items-center gap-1">
        <User className="h-3 w-3" />
        {job.team_members?.name ?? 'Unassigned'}
      </span>
      {job.client_name && <span className="truncate">· {job.client_name}</span>}
      {!isCleaner && <JobPaymentChip job={job} className="ml-0.5" />}
    </span>
  );

  const railSectionClass = 'adm-panel space-y-2.5 p-4';
  const railItemClass = 'rounded-xl border border-border bg-card px-3 py-2.5 text-sm transition-colors hover:border-primary/30';

  const summaryRail = (
    <>
      {/* Compact summary next to the calendar so the page is useful at a glance
          (QA: it looked empty). Reuses already-loaded data only — no extra
          queries. Shown for both Team and Week views. */}
      <div data-testid="schedule-summary-overview" className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 2xl:grid-cols-1">
        <p className="adm-label col-span-full px-1">Schedule Overview</p>
        {!isCleaner && (
          <section data-testid="schedule-summary-booking-requests" className={railSectionClass}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-[14px] font-semibold">Booking Requests</h3>
                <p className="text-[12px] text-muted-foreground">Waiting to be assigned or approved</p>
              </div>
              {bookingRequestsSummary.length > 0 && (
                <StatusChip tone="violet" dot={false}>{bookingRequestsSummary.length}</StatusChip>
              )}
            </div>
            {bookingRequestsSummary.length === 0 ? (
              <p className="text-[13px] text-muted-foreground py-1">
                No booking requests waiting for review.
              </p>
            ) : (
              <ul className="space-y-2">
                {bookingRequestsSummary.map((job) => (
                  <li
                    key={job.id}
                    data-testid={`booking-request-${job.id}`}
                    className={cn(railItemClass, 'flex flex-col gap-2.5')}
                  >
                    <div className="flex min-w-0 items-start gap-2.5">
                      <PersonAvatar name={job.client_name || job.title} size={28} />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-[13px] truncate block">{job.title}</span>
                        {renderJobMeta(job)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Select
                        value={job.team_member_id || 'unassigned'}
                        onValueChange={(v) => handleUpdateJobAssignment(job.id, v === 'unassigned' ? null : v)}
                        disabled={!canWrite}
                      >
                        <SelectTrigger className="h-8 min-w-0 flex-1 text-[12.5px]">
                          <SelectValue placeholder="Assign" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {teamMembers.map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className={cn(adminButton.outline, 'h-8 px-2.5')}
                        onClick={() => openJobDetails(job)}
                      >
                        View
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className={cn(adminButton.primary, 'h-8 px-3')}
                        onClick={() => handleApproveRequest(job)}
                        disabled={!canWrite}
                      >
                        Approve
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section data-testid="schedule-summary-this-week" className={railSectionClass}>
          <div>
            <h3 className="text-[14px] font-semibold">This week</h3>
            <p className="text-[12px] text-muted-foreground">
              {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d')}
            </p>
          </div>
          {thisWeekSummary.length === 0 ? (
            <p className="text-[13px] text-muted-foreground py-1">
              No jobs scheduled this week. Use “Add Job” to book one.
            </p>
          ) : (
            <ul className="space-y-2">
              {thisWeekSummary.map((job) => (
                <li
                  key={job.id}
                  data-testid={`summary-job-${job.id}`}
                  className={cn(railItemClass, 'flex items-start gap-2.5')}
                >
                  <span className={cn('adm-job-accent mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]', `adm-tone-${getStatusMeta(job.status).tone}`)} aria-hidden="true" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-semibold text-[13px] truncate">{job.title}</span>
                    {renderJobMeta(job)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="schedule-summary-upcoming" className={railSectionClass}>
          <div>
            <h3 className="text-[14px] font-semibold">Upcoming jobs</h3>
            <p className="text-[12px] text-muted-foreground">After this week</p>
          </div>
          {upcomingSummary.length === 0 ? (
            <p className="text-[13px] text-muted-foreground py-1">
              No upcoming jobs scheduled beyond this week yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {upcomingSummary.map((job) => (
                <li
                  key={job.id}
                  data-testid={`summary-job-${job.id}`}
                  className={cn(railItemClass, 'flex items-start gap-2.5')}
                >
                  <span className={cn('adm-job-accent mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]', `adm-tone-${getStatusMeta(job.status).tone}`)} aria-hidden="true" />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="font-semibold text-[13px] truncate">{job.title}</span>
                    {renderJobMeta(job)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {!isCleaner && (
        <section data-testid="schedule-summary-request-approvals" className={railSectionClass}>
          <div>
            <h3 className="text-[14px] font-semibold">Cancel/Reschedule Requests</h3>
            <p className="text-[12px] text-muted-foreground">Review customer changes and approve bookings back onto the schedule</p>
          </div>
          {cancelledOrRescheduledRequests.length === 0 ? (
            <p className="text-[13px] text-muted-foreground py-1">
              No cancelled or rescheduled requests waiting for approval.
            </p>
          ) : (
            <ul className="space-y-2">
              {cancelledOrRescheduledRequests.map((job) => (
                <li
                  key={job.id}
                  data-testid={`request-approval-${job.id}`}
                  className={cn(railItemClass, 'flex flex-col gap-2.5')}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-[13px] truncate">{job.title}</span>
                      <StatusChip status={job.status}>
                        {job.status === 'pending_confirmation' ? 'Rescheduled / pending' : job.status.replace('_', ' ')}
                      </StatusChip>
                    </div>
                    {renderJobMeta(job)}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(adminButton.outline, 'h-8 px-2.5')}
                      onClick={() => openJobDetails(job)}
                    >
                      View
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className={cn(adminButton.primary, 'h-8 px-3')}
                      onClick={() => handleApproveRequest(job)}
                      disabled={!canWrite}
                    >
                      Approve
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );

  const categoryResultsPanel = !isCleaner && categoryFilter ? (
    // Active category: replace the summary sections with a flat, cross-date
    // list of every matching job so the owner can glance across all dates.
    <section data-testid="schedule-category-results" className="adm-panel">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-[14px] font-semibold">
          {CATEGORY_LABELS[categoryFilter]} jobs
        </h2>
        <p className="text-[12px] text-muted-foreground">
          Every {CATEGORY_LABELS[categoryFilter].toLowerCase()} job across all dates
        </p>
      </div>
      <div className="p-3 md:p-4">
        {categoryLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading {CATEGORY_LABELS[categoryFilter].toLowerCase()} jobs…
          </div>
        ) : categoryResults.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No {CATEGORY_LABELS[categoryFilter].toLowerCase()} jobs found.
          </p>
        ) : (
          <ul className="adm-divide">
            {categoryResults.map((job) => (
              <li
                key={job.id}
                data-testid={`category-job-${job.id}`}
                className="flex flex-col gap-2 px-1 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <PersonAvatar
                    name={job.team_members?.name ?? null}
                    seed={job.team_member_id}
                    placeholder={!job.team_members?.name}
                    size={28}
                  />
                  <div className="min-w-0">
                    <span className="font-semibold text-[13px] truncate block">{job.title}</span>
                    {renderJobMeta(job)}
                  </div>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Select
                    value={job.status}
                    onValueChange={(v) => handleUpdateJobStatus(job.id, v)}
                    disabled={!canWrite}
                  >
                    <SelectTrigger
                      data-testid={`category-job-status-${job.id}`}
                      className="h-8 w-full text-[12.5px] sm:w-[170px]"
                      aria-label={`Change status for ${job.title}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(adminButton.outline, 'h-8 w-full sm:w-auto')}
                    onClick={() => openJobDetails(job)}
                  >
                    View
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  ) : null;

  const now = new Date();
  const weekNowOffset = (now.getHours() - 7) * 60 + now.getMinutes();
  const showWeekNowLine = weekNowOffset >= 0 && weekNowOffset <= HOURS.length * 60;

  const weekView = (
    <section className="adm-panel overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-3 md:px-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className={adminButton.icon} onClick={handlePrevWeek} aria-label="Previous week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className={cn(adminButton.outline, 'h-8')} onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" className={adminButton.icon} onClick={handleNextWeek} aria-label="Next week">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <h2 className="ml-1 text-[15px] font-semibold sm:hidden">
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'd')}
          </h2>
          <h2 className="ml-1 hidden text-[15px] font-semibold sm:block">
            {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
          </h2>
        </div>
        <div className="hidden flex-wrap items-center gap-1.5 lg:flex">
          <StatusChip status="scheduled">Scheduled</StatusChip>
          <StatusChip status="in_progress">In Progress</StatusChip>
          <StatusChip status="completed">Completed</StatusChip>
        </div>
      </div>
      <div className="adm-scroll overflow-x-auto">
        {/* Calendar Grid */}
        <div className="grid min-w-[760px]" style={{ gridTemplateColumns: '56px repeat(7, minmax(0, 1fr))' }}>
          {/* Time Column */}
          <div className="border-r border-border">
            <div className="h-14 border-b border-border" />
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="h-[60px] pr-2 text-right text-[11px] font-medium text-muted-foreground"
              >
                <span className="relative -top-[7px]">{format(new Date().setHours(hour, 0), 'h a').toLowerCase()}</span>
              </div>
            ))}
          </div>

          {/* Day Columns */}
          {weekDays.map((day, index) => {
            const dayJobs = getJobsForDay(day);
            const dayLanes = getJobLanes(dayJobs);
            const isToday = isSameDay(day, new Date());
            const hasHours = Boolean(getBusinessHoursForDay(day));

            return (
              <div key={index} className={cn('border-r border-border last:border-r-0', isToday && 'bg-secondary/40')}>
                {/* Day Header */}
                <div className="flex h-14 flex-col items-center justify-center gap-0.5 border-b border-border">
                  <p className={cn('text-[11px] font-medium uppercase tracking-wide', isToday ? 'text-primary' : 'text-muted-foreground')}>
                    {format(day, 'EEE')}
                  </p>
                  <p
                    className={cn(
                      'adm-num flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-[14px] font-semibold',
                      isToday ? 'bg-primary text-primary-foreground' : 'text-foreground'
                    )}
                  >
                    {format(day, 'd')}
                  </p>
                </div>

                {/* Time Slots */}
                <div className="relative" style={{ height: `${HOURS.length * 60}px` }}>
                  {/* Hour Lines with Open/Closed Background */}
                  {HOURS.map((hour) => {
                    const isOpen = isHourOpen(day, hour);
                    return (
                      <div
                        key={hour}
                        className={cn(
                          'adm-cal-hour absolute w-full',
                          !isOpen && hasHours && 'adm-stripes bg-muted/30'
                        )}
                        style={{ top: `${(hour - 7) * 60}px`, height: '60px' }}
                      />
                    );
                  })}

                  {isToday && showWeekNowLine && (
                    <div className="adm-now-line" style={{ top: weekNowOffset }} aria-hidden="true" />
                  )}

                  {/* Jobs */}
                  {dayJobs.map((job) => {
                    const position = getJobPosition(job);
                    const isOverlapping = overlappingJobIds.has(job.id);
                    const { lane, lanes } = dayLanes.get(job.id) ?? { lane: 0, lanes: 1 };
                    const widthPct = 100 / lanes;
                    const tone = getStatusMeta(job.status).tone;
                    // Many parallel lanes leave slivers: keep the block (and its
                    // accessible text) but hide the visible copy; hover shows it.
                    const compact = lanes >= 3;
                    return (
                      <div
                        key={job.id}
                        data-testid={`week-job-${job.id}`}
                        className={cn(
                          'adm-job absolute cursor-pointer overflow-hidden',
                          compact ? 'px-1 py-1' : 'px-2 py-1.5',
                          `adm-tone-${tone}`,
                          job.status === 'cancelled' && 'adm-job--cancelled',
                          isOverlapping && 'ring-2 ring-destructive ring-offset-1'
                        )}
                        style={{
                          ...position,
                          left: `calc(${widthPct * lane}% + 3px)`,
                          width: `calc(${widthPct}% - 6px)`,
                        }}
                        data-lane={lane}
                        data-lanes={lanes}
                        data-overlapping={isOverlapping || undefined}
                        title={`${job.title} · ${job.start_time.slice(0, 5)} - ${job.end_time.slice(0, 5)}`}
                        onClick={() => {
                          // Week jobs load every column: no series_id = one-off.
                          setSelectedJob({ ...job, series_id: job.series_id ?? null });
                          setIsEditing(false);
                          setAddCleanerId('');
                          setIsViewDialogOpen(true);
                        }}
                      >
                        <p className="adm-job-title text-[11.5px] font-semibold leading-tight flex items-center gap-1">
                          {isOverlapping && (
                            <AlertTriangle className="h-3 w-3 shrink-0 text-destructive" aria-label="Overlapping schedule" />
                          )}
                          <span className={cn('truncate', compact && 'sr-only')}>{job.title}</span>
                          {job.series_id && (
                            <Repeat
                              className="h-3 w-3 shrink-0 text-muted-foreground"
                              aria-label="Repeat clean"
                              data-testid={`week-job-repeat-${job.id}`}
                            />
                          )}
                        </p>
                        <p className={cn('adm-job-ink mt-0.5 text-[10.5px] font-medium leading-tight truncate', compact && 'sr-only')}>
                          {job.start_time.slice(0, 5)} - {job.end_time.slice(0, 5)}
                        </p>
                        {(() => {
                          const cleanerNames = getAssignedMemberNames(job, teamMembers);
                          return (
                            <p
                              data-testid={`week-job-cleaners-${job.id}`}
                              className={cn('mt-0.5 text-[10.5px] leading-tight text-muted-foreground truncate flex items-center gap-1', compact && 'sr-only')}
                            >
                              <User className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="truncate">
                                {cleanerNames.length > 0 ? cleanerNames.join(', ') : 'Unassigned'}
                              </span>
                            </p>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );

  return (
    <div className="space-y-4 p-4 md:p-6 lg:p-8">
      {isCleaner && (
        <Button asChild variant="ghost" size="sm" className={cn(adminButton.ghost, '-ml-2')}>
          <Link to="/admin">
            <ChevronLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>
      )}

      {/* Toolbar: view switch, cleaner filter, status categories, conflicts, Add Job */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="h-9 rounded-[10px] border border-border bg-muted p-[3px]">
              <TabsTrigger value="team" className="h-[28px] gap-1.5 rounded-[7px] px-3 text-[13px] data-[state=active]:shadow-[var(--adm-shadow-sm)]">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Team</span>
              </TabsTrigger>
              <TabsTrigger value="week" className="h-[28px] gap-1.5 rounded-[7px] px-3 text-[13px] data-[state=active]:shadow-[var(--adm-shadow-sm)]">
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Week</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {viewMode === 'team' && !isCleaner && (
            <Select value={selectedMemberId || 'all'} onValueChange={(v) => setSelectedMemberId(v === 'all' ? null : v)}>
              <SelectTrigger className="h-9 w-[170px] bg-card text-[13px]">
                <SelectValue placeholder="All cleaners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cleaners</SelectItem>
                {teamMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Compact global overlap indicator: surfaces upcoming double-bookings for
              the same team member across the next 90 days, so owners notice future
              conflicts from any week without a large full-width banner (issue #34). */}
          {upcomingConflicts.count > 0 && (
            <button
              type="button"
              onClick={handleJumpToNextConflict}
              data-testid="overlap-alert"
              aria-label={`${upcomingConflicts.count} upcoming ${upcomingConflicts.count === 1 ? 'conflict' : 'conflicts'} — jump to next conflict`}
              className="inline-flex h-9 w-fit max-w-full items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/[0.06] px-3 text-[13px] text-destructive transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="font-semibold">
                {upcomingConflicts.count} upcoming {upcomingConflicts.count === 1 ? 'conflict' : 'conflicts'}
              </span>
              {upcomingConflicts.nextDate && (
                <span className="text-destructive/80">
                  · next {format(parseISO(upcomingConflicts.nextDate), 'EEE, MMM d')}
                </span>
              )}
            </button>
          )}

        {/* Owner/admin category buttons (issue #49): a cross-date glance of jobs by
            status, kept separate from the dispatch calendar so the grid stays easy
            to navigate. While a category is active it replaces the summary
            sections with a single matching-jobs list. */}
        {!isCleaner && (
          <div
            data-testid="schedule-category-filters"
            className="flex flex-wrap items-center gap-1.5 lg:ml-1"
          >
            {/* "All" clears any active category, restoring the summary sections.
                It is the selected state whenever no category filter is active. */}
            <button
              type="button"
              data-testid="schedule-category-all"
              aria-pressed={categoryFilter === null}
              onClick={() => setCategoryFilter(null)}
              className={cn(
                'flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                categoryFilter === null
                  ? 'border-primary/30 bg-secondary text-secondary-foreground'
                  : 'border-border bg-card text-muted-foreground hover:text-foreground'
              )}
            >
              All
            </button>
            {CATEGORY_FILTERS.map((f) => {
              const isActive = categoryFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`schedule-category-${f.key}`}
                  aria-pressed={isActive}
                  onClick={() => setCategoryFilter((prev) => (prev === f.key ? null : f.key))}
                  className={cn(
                    'flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    isActive
                      ? 'border-primary/30 bg-secondary text-secondary-foreground'
                      : 'border-border bg-card text-muted-foreground hover:text-foreground'
                  )}
                >
                  <span className={cn('adm-job-accent h-2 w-2 rounded-full', `adm-tone-${getStatusMeta(f.key).tone}`)} aria-hidden="true" />
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
          <div className="flex-1" aria-hidden="true" />

          {!isCleaner && (
          <Dialog open={isAddDialogOpen} onOpenChange={(open) => { setIsAddDialogOpen(open); if (!open) setPrefillQuoteId(null); }}>
            <DialogTrigger asChild>
              <Button size="sm" className={adminButton.primary} disabled={!canWrite}>
                <Plus className="h-4 w-4" />
                Add Job
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Job</DialogTitle>
              <DialogDescription>Schedule a new job and assign a team member</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="col-span-2 space-y-2">
                <Label>Job Title *</Label>
                <Input
                  value={newJob.title}
                  onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                  placeholder="e.g., Deep Clean - 3 Bed House"
                />
              </div>
              <div className="space-y-2">
                <Label>Service</Label>
                <Select
                  value={newJob.service_id}
                  onValueChange={(v) => setNewJob({ ...newJob, service_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assign Team Member</Label>
                <Select
                  value={newJob.team_member_id}
                  onValueChange={(v) => setNewJob({ ...newJob, team_member_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cleaners needed</Label>
                <Input
                  type="number"
                  min={1}
                  value={newJob.required_cleaners}
                  onChange={(e) => setNewJob({ ...newJob, required_cleaners: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Assign more cleaners from the job after it's created.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !selectedDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        if (date) setNewJob({ ...newJob, scheduled_date: format(date, 'yyyy-MM-dd') });
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={newJob.start_time}
                    onChange={(e) => setNewJob({ ...newJob, start_time: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={newJob.end_time}
                    onChange={(e) => setNewJob({ ...newJob, end_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Client Name</Label>
                <Input
                  value={newJob.client_name}
                  onChange={(e) => setNewJob({ ...newJob, client_name: e.target.value })}
                  placeholder="Client name"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={newJob.client_email}
                  onChange={(e) => setNewJob({ ...newJob, client_email: e.target.value })}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Phone</Label>
                <Input
                  value={newJob.client_phone}
                  onChange={(e) => setNewJob({ ...newJob, client_phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Address</Label>
                <AddressAutocomplete
                  value={newJob.address}
                  onChange={(value) => setNewJob({ ...newJob, address: value })}
                  placeholder="Full address"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={newJob.notes}
                  onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
              <RepeatSettingsFields
                className="col-span-2"
                value={newJobRepeat}
                onChange={setNewJobRepeat}
                firstVisit={newJob.scheduled_date}
                idPrefix="new-job-repeat"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddJob} disabled={!canWrite || saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Job
              </Button>
            </div>
          </DialogContent>
        </Dialog>
          )}
        </div>

      </div>

      <div
        className={cn(
          'grid grid-cols-1 gap-4',
          !(categoryFilter && !isCleaner) && '2xl:grid-cols-[minmax(0,1fr)_340px]'
        )}
      >
        <div className="min-w-0 space-y-4">
          {categoryResultsPanel}

          {/* Team View */}
          {viewMode === 'team' && (
            <DispatchCalendar
              readOnly={!canWrite || isCleaner}
              selectedMemberId={isCleaner ? (cleanerTeamMemberId ?? 'none') : selectedMemberId}
              initialDate={searchParams.get('date')}
              initialFilter={isCleaner ? null : searchParams.get('filter')}
              focusJobId={focusJobId}
            />
          )}

          {/* Week View */}
          {viewMode === 'week' && weekView}
        </div>

        {!(categoryFilter && !isCleaner) && (
          <aside className="order-first min-w-0 space-y-4 lg:order-none" aria-label="Schedule summary">
            {summaryRail}
          </aside>
        )}
      </div>

      {/* View/Edit Job Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.title}</DialogTitle>
            <DialogDescription>
              {selectedJob && format(parseISO(selectedJob.scheduled_date), 'EEEE, MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>
          {selectedJob && !isEditing && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  {selectedJob.start_time.slice(0, 5)} – {selectedJob.end_time.slice(0, 5)}
                </div>
                <StatusChip status={selectedJob.status}>
                  {selectedJob.status.replace('_', ' ')}
                </StatusChip>
                {!isCleaner && <JobPaymentChip job={selectedJob} />}
              </div>

              {selectedJob.address && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span>{selectedJob.address}</span>
                </div>
              )}

              {selectedJob.client_name && (
                <div className="text-sm">
                  <p className="font-medium">{selectedJob.client_name}</p>
                  {selectedJob.client_email && (
                    <p className="text-muted-foreground">{selectedJob.client_email}</p>
                  )}
                  {selectedJob.client_phone && (
                    <p className="text-muted-foreground">{selectedJob.client_phone}</p>
                  )}
                </div>
              )}

              {selectedJob.services?.name && (
                <div className="space-y-1 text-sm">
                  <Label>Service</Label>
                  <p className="text-muted-foreground">{selectedJob.services.name}</p>
                </div>
              )}

              {!isCleaner && (() => {
                const assignedIds = getAssignedMemberIds(selectedJob);
                const required = getRequiredCleaners(selectedJob);
                const assignedMembers = assignedIds
                  .map((id) => teamMembers.find((m) => m.id === id))
                  .filter((m): m is TeamMember => Boolean(m));
                const unassigned = teamMembers.filter((m) => !assignedIds.includes(m.id));
                const understaffed = assignedIds.length < required;

                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label>Cleaners</Label>
                      <div className="flex items-center gap-2">
                        {required > 1 && (
                          <span className="text-xs font-medium text-muted-foreground">
                            Needs {required} cleaners
                          </span>
                        )}
                        <span
                          data-testid="cleaner-count"
                          className={cn(
                            'text-xs font-semibold',
                            understaffed ? 'text-destructive' : 'text-emerald-600'
                          )}
                        >
                          {assignedIds.length}/{required} assigned
                        </span>
                      </div>
                    </div>

                    {assignedMembers.length > 0 ? (
                      <ul className="space-y-1">
                        {assignedMembers.map((m) => (
                          <li
                            key={m.id}
                            className="flex items-center justify-between rounded-md border px-2 py-1 text-sm"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <span className="truncate">{m.name}</span>
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0"
                              aria-label={`Remove ${m.name}`}
                              onClick={() => handleRemoveCleaner(selectedJob.id, m.id)}
                              disabled={!canWrite || saving}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-muted-foreground">No cleaners assigned yet.</p>
                    )}

                    {unassigned.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Select value={addCleanerId} onValueChange={setAddCleanerId} disabled={!canWrite}>
                          <SelectTrigger data-testid="add-cleaner-select" className="flex-1">
                            <SelectValue placeholder="Add a cleaner" />
                          </SelectTrigger>
                          <SelectContent>
                            {unassigned.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          className="gap-1"
                          onClick={() => handleAddCleaner(selectedJob.id, addCleanerId)}
                          disabled={!canWrite || !addCleanerId || saving}
                        >
                          <Plus className="h-4 w-4" />
                          Add cleaner
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {!isCleaner && (
                <RepeatVisitPanel
                  job={selectedJob}
                  canWrite={canWrite}
                  onChanged={() => fetchData()}
                  onClose={() => setIsViewDialogOpen(false)}
                  onResolved={handleSeriesLinkResolved}
                />
              )}

              {!isCleaner && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={selectedJob.status}
                    onValueChange={(v) => handleUpdateJobStatus(selectedJob.id, v)}
                    disabled={!canWrite}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedJob.notes && (
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded">
                    {selectedJob.notes}
                  </p>
                </div>
              )}

              <JobWorkPanel job={selectedJob} onChanged={() => fetchData()} />

              {isCleaner ? (
                <div className="flex justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              ) : (
                <div data-testid="job-detail-actions" className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-w-24 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteJob(selectedJob.id)}
                    disabled={!canWrite}
                  >
                    Delete Job
                  </Button>
                  <Button variant="outline" size="sm" onClick={startEditJob} disabled={!canWrite} className="min-w-24 gap-2">
                    <Pencil className="h-4 w-4" />
                    Edit Job
                  </Button>
                  <Button variant="outline" size="sm" className="min-w-24" onClick={() => setIsViewDialogOpen(false)}>
                    Close
                  </Button>
                  {(['pending_confirmation', 'pending confirmation', 'cancelled'].includes(selectedJob.status)) && (
                    <Button size="sm" className="min-w-24" onClick={() => handleApproveRequest(selectedJob)} disabled={!canWrite}>
                      Approve
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {selectedJob && isEditing && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="week-edit-title">Title</Label>
                <Input
                  id="week-edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Job title"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="week-edit-date">Date</Label>
                  <Input
                    id="week-edit-date"
                    type="date"
                    value={editForm.scheduled_date}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, scheduled_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="week-edit-service">Service</Label>
                  <Select
                    value={editForm.service_id || 'none'}
                    onValueChange={(v) => setEditForm((prev) => ({ ...prev, service_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger id="week-edit-service">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="week-edit-start">Start time</Label>
                  <Input
                    id="week-edit-start"
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="week-edit-end">End time</Label>
                  <Input
                    id="week-edit-end"
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="week-edit-client-name">Client name</Label>
                <Input
                  id="week-edit-client-name"
                  value={editForm.client_name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Client name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="week-edit-client-phone">Phone</Label>
                  <Input
                    id="week-edit-client-phone"
                    value={editForm.client_phone}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="Phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="week-edit-client-email">Email</Label>
                  <Input
                    id="week-edit-client-email"
                    type="email"
                    value={editForm.client_email}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, client_email: e.target.value }))}
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="week-edit-address">Address</Label>
                <Input
                  id="week-edit-address"
                  value={editForm.address}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, address: e.target.value }))}
                  placeholder="Address"
                />
              </div>

              <p className="text-xs text-muted-foreground">
                Team member, status and notes are managed from the job details view.
              </p>

              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={() => handleSaveEdit()} disabled={!canWrite || saving} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EditScopeDialog
        open={scopeOpen}
        onOpenChange={(open) => !scopeBusy && setScopeOpen(open)}
        onChoose={(scope) => handleSaveEdit(scope)}
        futureDisabled={selectedJob?.status === 'completed' || selectedJob?.status === 'cancelled'}
        busy={scopeBusy}
      />

      <ScheduleClashDialog {...clashPrompt} />
    </div>
  );
};

export default ScheduleAdmin;
